import React, { useState, useEffect } from 'react';
import './ShoppingCart.css';

const CART_STORAGE_KEY = 'uselessProductsCart';

const formatPrice = (value) => {
    return Number(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
};

const loadCartObject = () => {
    if (typeof window === 'undefined') return {};
    try {
        return JSON.parse(window.sessionStorage.getItem(CART_STORAGE_KEY) || '{}');
    } catch {
        return {};
    }
};

const saveCartObject = (cart) => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
};

function ShoppingCart({ theme, onBackClick, onCartUpdated, onPlaceOrderClick }) {
    const [cartItems, setCartItems] = useState([]);

    useEffect(() => {
        const stored = loadCartObject();
        setCartItems(Object.values(stored));
    }, []);

    const handleRemove = (id) => {
        const cart = loadCartObject();
        delete cart[id];
        saveCartObject(cart);
        setCartItems(Object.values(cart));
        if (typeof onCartUpdated === 'function') {
            onCartUpdated();
        }
    };

    const handleClearAll = () => {
        saveCartObject({});
        setCartItems([]);
        if (typeof onCartUpdated === 'function') {
            onCartUpdated();
        }
    };

    const totalPrice = cartItems.reduce((sum, item) => {
        return sum + Number(item.price || 0) * Number(item.quantity || 0);
    }, 0);

    return (
        <div className={`app ${theme}`}>
            <main>
                <section className="cart-header-section">
                    <div className="cart-header">
                        <div>
                            <h1>Shopping Cart</h1>
                            <p className="cart-count">
                                {cartItems.length === 0
                                    ? 'Your cart is pleasantly empty'
                                    : `${cartItems.length} item${cartItems.length !== 1 ? 's' : ''} in cart`}
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Continue Shopping
                        </button>
                    </div>
                </section>

                <section className="cart-content">
                    {cartItems.length === 0 ? (
                        <div className="empty-cart">
                            <div className="empty-cart-icon">🛒</div>
                            <h2>Your Cart is Empty</h2>
                            <p>
                                Looks like you haven't added any brilliantly useless products yet.
                                Head back to the gallery to discover items that absolutely shouldn't exist.
                            </p>
                            <button className="refresh-btn" onClick={onBackClick}>
                                Start Shopping
                            </button>
                        </div>
                    ) : (
                        <div className="cart-items">
                            {cartItems.map((item) => (
                                <div key={item.id} className="cart-item">
                                    {item.img ? (
                                        <img src={item.img} alt={item.name} />
                                    ) : (
                                        <div className="cart-item-fallback">
                                            No image available
                                        </div>
                                    )}
                                    <div className="item-details">
                                        <h3>{item.name}</h3>
                                        <p>{item.desc}</p>
                                        <p className="item-price">Unit: {formatPrice(item.price)}</p>
                                        <p className="item-quantity">Quantity: {item.quantity}</p>
                                        <p className="item-line-total">
                                            Total: {formatPrice(Number(item.price || 0) * Number(item.quantity || 0))}
                                        </p>
                                    </div>
                                    <button className="remove-btn" onClick={() => handleRemove(item.id)}>
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <div className="cart-summary">
                                <div className="cart-summary-actions">
                                    <button className="remove-all-btn" onClick={handleClearAll}>
                                        Remove All
                                    </button>
                                </div>
                                <h3>Order Summary</h3>
                                <p>
                                    <span>Subtotal</span>
                                    <span>{formatPrice(totalPrice)}</span>
                                </p>
                                <p>
                                    <span>Shipping</span>
                                    <span>{formatPrice(0)}</span>
                                </p>
                                <p className="total">
                                    <span>Total</span>
                                    <span>{formatPrice(totalPrice)}</span>
                                </p>
                                <button className="refresh-btn" onClick={onPlaceOrderClick}>
                                    Proceed to Checkout
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default ShoppingCart;
